// w31 Buklama — pop-up / tunnel-book wedding preview card.
//
// The full template is a paper-diorama hero (midnight folds, gold stars).
// For the catalog card we trade the dark night for the brand's warm
// cream paper (#F5EFE6) so the card sits comfortably alongside the rest
// of the catalog grid, but we keep the paper-fold idea: the outer frame
// is a soft cream→tan paper-fold gradient (a hint of cast shadow at the
// folds), the inner card is the same paper at full saturation, and the
// focal element is a circular medallion holding a layered paper-fold
// arch motif in template gold + sage. Names sit in Cormorant Garamond
// italic; the eyebrow / footer use the standard small-caps treatment.
import { Photo, type LayoutCtx } from "./_shared";

// Paper-fold gradient — cream highlight, soft tan crease, cream again.
// Mimics a folded-card cover catching warm light.
const FOLD_GRADIENT =
  "linear-gradient(135deg, #F5EFE6 0%, #E6D8BC 18%, #F5EFE6 38%, #E6D8BC 62%, #F5EFE6 82%, #D9C8A6 100%)";

export function BuklamaPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  const captionDate = formatBuklamaDate(date);
  const [a, b] = names.split(/\s*&\s*/);

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FOLD_GRADIENT,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TO'Y MAROSIMI
        </div>

        {/* Photo medallion with paper-fold arch overlay */}
        <div
          style={{
            width: "60%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: FOLD_GRADIENT,
              padding: "5%",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: bg,
                padding: "4%",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <Photo
                  src={photo}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                  fallback={
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: `radial-gradient(circle at 50% 60%, ${accent}33, ${bg})`,
                      }}
                    />
                  }
                />
                {/* Layered paper arches — the tunnel-book signature */}
                <svg
                  viewBox="0 0 100 100"
                  preserveAspectRatio="xMidYMid slice"
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                  aria-hidden
                >
                  <path d="M0 100 V62 C18 50 38 44 50 44 C62 44 82 50 100 62 V100Z"
                    fill="#7E926A" opacity="0.55" />
                  <path d="M0 100 V72 C20 64 38 60 50 60 C62 60 80 64 100 72 V100Z"
                    fill={accent} opacity="0.4" />
                  <path d="M0 100 V84 C20 78 38 76 50 76 C62 76 80 78 100 84 V100Z"
                    fill={ink} opacity="0.32" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Names */}
        <div style={{ fontFamily: font, fontSize: 24, lineHeight: 1.05, color: ink, fontStyle: "italic" }}>
          {a ?? names}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            letterSpacing: "0.42em",
            color: accent,
            margin: "4px 0",
          }}
        >
          VA
        </div>
        <div style={{ fontFamily: font, fontSize: 24, lineHeight: 1.05, color: ink, fontStyle: "italic" }}>
          {b ?? ""}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          NIKOH TANTANASI · {captionDate}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}

function formatBuklamaDate(d: string): string {
  const m = d.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return d.toUpperCase();
  const day = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const year = m[3];
  const months = [
    "YANVAR", "FEVRAL", "MART", "APREL", "MAY", "IYUN",
    "IYUL", "AVGUST", "SENTABR", "OKTABR", "NOYABR", "DEKABR",
  ];
  return `${day} ${months[month - 1] ?? ""} ${year}`;
}
